import type { ReactNode } from 'react';
import { Chip, Panel } from '../../kit';
import type { ChipTone } from '../../kit';
import { Failure, Loading, When } from '../../bits';
import type { ReadState } from '../../context';
import { useLocale, useT } from '../../../messages';
import type { T } from '../../../messages';
import type { HealthVerdict, TenantHealthRead, TenantReadiness } from '../../../wire';
import type { Go } from './steps';
import { follow } from './go';
import { reasonLabel } from '../activity/next';
import type { Moves } from './go';

/**
 * **"Right now"** — the Health card, redesigned (console redesign B1).
 *
 * The old card was a table: one row per fact, every row with its own "as of",
 * raw counts and a domain status token. What an operator needed from it was
 * three things in order — is anything wrong, what, and what do I do about it —
 * and the table answered the first with a pill and left the other two to them.
 *
 * Now: one verdict chip; four plain signals (their book, their texts, requests
 * waiting, their website); and when the verdict has reasons, **each reason as a
 * sentence with its fix** — a button to where it is fixed, or a sentence
 * saying who fixes it when the console cannot. One "Refreshed" note for the
 * whole panel, since it is one read at one instant. Everything the old table
 * printed is still here, in words, under "Details" (plan principle 4).
 *
 * The verdict and its reasons are still the server's (`repos/health.ts`
 * decides them once); this file only says them.
 */

function verdictTone(verdict: HealthVerdict): ChipTone {
  switch (verdict) {
    case 'quiet':
      return 'ok';
    case 'attention':
      return 'warn';
    case 'failing':
      return 'danger';
  }
}

function verdictWords(t: T, verdict: HealthVerdict): string {
  switch (verdict) {
    case 'quiet':
      return t('overview.verdict.quiet');
    case 'attention':
      return t('overview.verdict.attention');
    case 'failing':
      return t('overview.verdict.failing');
  }
}

interface Reason {
  key: string;
  sentence: string;
  fix: { label: string; go: Go } | { hint: string } | null;
}

const CONNECTIONS: Go = { kind: 'tab', tab: 'connections' };

/**
 * A server reason as a sentence and its fix. The words are the businesses
 * list's (`reasonLabel`, the console's one wording of a health reason), set as
 * a sentence; the fix is this panel's. Unknown reasons still say something.
 */
export function reasonOf(t: T, reason: string): Reason {
  const sentence = asSentence(reasonLabel(t, reason));
  const toConnections = { label: t('overview.fix.connections'), go: CONNECTIONS };
  switch (reason) {
    case 'webhook_exhausted':
    case 'endpoint_failing':
    case 'webhook_failed':
    case 'webhook_stuck':
      return { key: reason, sentence, fix: toConnections };
    case 'text_exhausted':
    case 'text_failed':
    case 'text_stuck':
      return { key: reason, sentence, fix: { hint: t('overview.fix.texts') } };
    case 'request_overdue':
      return { key: reason, sentence, fix: { hint: t('overview.fix.requests') } };
    case 'site_not_served':
      return {
        key: reason,
        sentence,
        fix: { label: t('overview.fix.site'), go: { kind: 'tab', tab: 'site' } },
      };
    default:
      return { key: reason, sentence: t('overview.reason.unknown'), fix: null };
  }
}

/** "booking updates … stopped arriving" as a sentence: capital, full stop. */
function asSentence(words: string): string {
  return words === '' ? words : `${words.charAt(0).toUpperCase()}${words.slice(1)}.`;
}

/** "Aug 25" in the console's language — a feed date, not a timestamp. */
export function shortDate(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

function domainWords(t: T, status: string | null): string {
  switch (status) {
    case 'pending_dns':
      return t('overview.domain.pending_dns');
    case 'issuing':
      return t('overview.domain.issuing');
    case 'live':
      return t('overview.domain.live');
    default:
      return status ?? '';
  }
}

function Signal({ title, main, note }: { title: string; main: string; note: string }): ReactNode {
  return (
    <div className="ov-signal">
      <h4>{title}</h4>
      <p>{main}</p>
      <small>{note === '' ? ' ' : note}</small>
    </div>
  );
}

export function RightNow({
  health,
  readiness,
  onReread,
  moves,
}: {
  health: ReadState<TenantHealthRead>;
  readiness: TenantReadiness | null;
  onReread: () => void;
  moves: Moves;
}): ReactNode {
  const t = useT();
  const locale = useLocale();
  const h = health.value;

  const reasons: Reason[] = h === null ? [] : h.reasons.map((r) => reasonOf(t, r));
  // The partner-key row moved to Connections (the mockup's note); what stays
  // here is the one case where its absence is a fault — a concierge that is
  // connected and holds no key cannot reach the business at all.
  if (h !== null && readiness !== null && readiness.concierge && !h.key.present) {
    reasons.push({
      key: 'no_key',
      sentence: t('overview.reason.noKey'),
      fix: { label: t('overview.fix.connections'), go: CONNECTIONS },
    });
  }

  const textsOff =
    readiness !== null && !readiness.texts.reminders && readiness.texts.owner !== 'partner';

  return (
    <Panel
      title={t('overview.now.title')}
      className="ov-now"
      actions={
        h === null ? undefined : (
          <Chip tone={verdictTone(h.verdict)}>{verdictWords(t, h.verdict)}</Chip>
        )
      }
    >
      {health.failure !== null ? <Failure failure={health.failure} /> : null}
      {h === null ? (
        health.loading ? (
          <Loading what={t('overview.loading.now')} />
        ) : null
      ) : (
        <>
          <div className="ov-signals">
            <Signal
              title={t('overview.signal.book')}
              main={
                h.book.next_seven_days === 0 && h.book.last_booking_at === null
                  ? t('overview.signal.book.none')
                  : t('overview.signal.book.week', { count: h.book.next_seven_days })
              }
              note={
                h.book.last_booking_at === null
                  ? t('overview.signal.book.never')
                  : t('overview.signal.book.last', {
                      when: shortDate(h.book.last_booking_at, locale),
                    })
              }
            />
            <Signal
              title={t('overview.signal.texts')}
              main={
                textsOff && h.texts.sent === 0 && h.texts.failed === 0
                  ? t('overview.signal.texts.off')
                  : t('overview.signal.texts.sent', { count: h.texts.sent })
              }
              note={
                textsOff && h.texts.sent === 0 && h.texts.failed === 0
                  ? t('overview.signal.texts.offNote')
                  : t('overview.signal.texts.failed', {
                      count: h.texts.failed + h.texts.exhausted,
                    })
              }
            />
            <Signal
              title={t('overview.signal.requests')}
              main={
                h.requests.waiting === 0 && h.requests.overdue === 0
                  ? t('overview.signal.requests.none')
                  : t('overview.signal.requests.waiting', { count: h.requests.waiting })
              }
              note={
                h.requests.overdue === 0
                  ? ''
                  : t('overview.signal.requests.overdue', { count: h.requests.overdue })
              }
            />
            <Signal
              title={t('overview.signal.site')}
              main={
                h.site.hostname === null
                  ? t('overview.signal.site.default')
                  : h.site.served
                    ? t('overview.signal.site.up')
                    : t('overview.signal.site.notUp')
              }
              note={h.site.hostname ?? ''}
            />
          </div>

          {reasons.length === 0 ? null : (
            <ul className="ov-reasons">
              {reasons.map((r) => (
                <li key={r.key}>
                  <span>{r.sentence}</span>
                  {r.fix === null ? null : 'go' in r.fix ? (
                    <button
                      type="button"
                      className="button button--sm"
                      onClick={() => {
                        if (r.fix !== null && 'go' in r.fix) follow(moves, r.fix.go);
                      }}
                    >
                      {r.fix.label}
                    </button>
                  ) : (
                    <small className="muted">{r.fix.hint}</small>
                  )}
                </li>
              ))}
            </ul>
          )}

          <details className="ov-details">
            <summary>{t('overview.details')}</summary>
            <HealthDetails h={h} />
          </details>

          <div className="ov-now__foot">
            <span className="muted">
              {t('overview.refreshed')} <When iso={h.as_of} />
            </span>
            <button
              type="button"
              className="button button--quiet button--sm"
              onClick={onReread}
              disabled={health.loading}
            >
              {t('overview.reread')}
            </button>
          </div>
        </>
      )}
    </Panel>
  );
}

/**
 * Every fact the old Health table printed, in words — the site's address and
 * its state, the text and update queues with their oldest waiting, requests,
 * the book, and the partner key with its last use. Kept so nothing the card
 * showed is lost (plan principle 4); folded because an operator reads it only
 * when a verdict sends them looking.
 */
function HealthDetails({ h }: { h: TenantHealthRead }): ReactNode {
  const t = useT();
  return (
    <dl className="ov-facts">
      <dt>{t('overview.details.site')}</dt>
      <dd>
        {h.site.hostname === null ? (
          t('overview.details.site.default')
        ) : (
          <>
            <span className="mono">{h.site.hostname}</span> · {domainWords(t, h.site.status)} ·{' '}
            {h.site.served
              ? t('overview.details.site.reachable')
              : t('overview.details.site.unreachable')}
            {h.site.verified_at === null ? null : (
              <>
                {' · '}
                {t('overview.details.site.verified')} <When iso={h.site.verified_at} />
              </>
            )}
            {h.site.updated_at === null ? null : (
              <>
                {' · '}
                {t('overview.details.site.changed')} <When iso={h.site.updated_at} />
              </>
            )}
          </>
        )}
        <div className="muted">{t('overview.details.site.note')}</div>
      </dd>

      <dt>{t('overview.details.texts')}</dt>
      <dd>
        {t('overview.details.queue', {
          hours: h.texts.window_hours,
          sent: h.texts.sent,
          queued: h.texts.queued,
          failed: h.texts.failed,
          exhausted: h.texts.exhausted,
        })}
        {h.texts.oldest_queued_at === null ? null : (
          <>
            {' · '}
            {t('overview.details.oldestWaiting')} <When iso={h.texts.oldest_queued_at} />
          </>
        )}
      </dd>

      <dt>{t('overview.details.updates')}</dt>
      <dd>
        {t('overview.details.queue', {
          hours: h.webhooks.window_hours,
          sent: h.webhooks.sent,
          queued: h.webhooks.queued,
          failed: h.webhooks.failed,
          exhausted: h.webhooks.exhausted,
        })}
        {' · '}
        {t('overview.details.endpoints', { count: h.webhooks.active_endpoints })}
        {h.webhooks.endpoint_failing ? <> · {t('overview.details.endpointFailing')}</> : null}
        {h.webhooks.oldest_queued_at === null ? null : (
          <>
            {' · '}
            {t('overview.details.oldestWaiting')} <When iso={h.webhooks.oldest_queued_at} />
          </>
        )}
        {h.webhooks.newest_exhausted_at === null ? null : (
          <>
            {' · '}
            {t('overview.details.newestGaveUp')} <When iso={h.webhooks.newest_exhausted_at} />
          </>
        )}
      </dd>

      <dt>{t('overview.details.requests')}</dt>
      <dd>
        {t('overview.details.requests.counts', {
          waiting: h.requests.waiting,
          overdue: h.requests.overdue,
        })}
        {h.requests.oldest_overdue_at === null ? null : (
          <>
            {' · '}
            {t('overview.details.requests.oldest')} <When iso={h.requests.oldest_overdue_at} />
          </>
        )}
      </dd>

      <dt>{t('overview.details.book')}</dt>
      <dd>
        {t('overview.details.book.week', { count: h.book.next_seven_days })}
        {' · '}
        {h.book.last_booking_at === null ? (
          t('overview.details.book.never')
        ) : (
          <>
            {t('overview.details.book.last')} <When iso={h.book.last_booking_at} />
          </>
        )}
      </dd>

      <dt>{t('overview.details.key')}</dt>
      <dd>
        {h.key.present ? (
          <>
            <span className="mono">{h.key.prefix}</span>
            {h.key.integration === null ? null : <> · {h.key.integration}</>}
            {' · '}
            {h.key.last_seen_at === null ? (
              t('overview.details.key.never')
            ) : (
              <>
                {t('overview.details.key.lastUsed')} <When iso={h.key.last_seen_at} />
              </>
            )}
            <div className="muted">{t('overview.details.key.batch')}</div>
          </>
        ) : (
          t('overview.details.key.none')
        )}
      </dd>
    </dl>
  );
}
